/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ShellFlyout, StablePortal, useShellFlyoutEnterDelay, useShellFlyoutHold, useShellFlyoutPresence } from '@/components/layout/shell'
import { useShellPanelExitClass } from '@/hooks/use-shell-panel-motion'
import { SHELL_ICON_RAIL_CLASS, SHELL_ICON_RAIL_PX } from '@/lib/shell-chrome'
import { SHELL_FLYOUT_ENTER_HOLD_MS, SHELL_PANEL_EASE, SHELL_PANEL_MOTION_MS } from '@/lib/shell-panel-motion'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import {
  SHELL_AGENT_FLOOR_PX,
  SHELL_LEFT_FLOOR_PX,
  resolveShellColumnWidth,
} from './resizable-layout.utils'

const FLYOUT_WIDTH_PX = SHELL_LEFT_FLOOR_PX - SHELL_ICON_RAIL_PX

type ShellChromeValue = {
  autoHide: boolean
  toggleAutoHide: () => void
  onMenuOpenChange: (open: boolean) => void
  closeFlyoutNow: () => void
  /** Open at once. Rail icon clicks use this. No-op unless auto-hide is on. */
  openFlyout: () => void
  /** Pointer is on the rail. Cancels a pending close. Does not open. */
  onRailEnter: () => void
  onRailLeave: () => void
  leftDisabledReason?: string
  agentDisabledReason?: string
  /** Auto-hide is on and the pointer is holding the flyout open. */
  flyoutOpen: boolean
  flyoutPortal: HTMLElement | null
  setFlyoutPortal: (node: HTMLElement | null) => void
}

const ShellChromeContext = createContext<ShellChromeValue>({
  autoHide: false,
  toggleAutoHide: () => {},
  onMenuOpenChange: () => {},
  closeFlyoutNow: () => {},
  openFlyout: () => {},
  onRailEnter: () => {},
  onRailLeave: () => {},
  flyoutOpen: false,
  flyoutPortal: null,
  setFlyoutPortal: () => {},
})

/** Shell chrome flags and the flyout hold shared by the rail, mode menu, and agent toggle. */
export function useShellChrome(): ShellChromeValue {
  return useContext(ShellChromeContext)
}

/**
 * Auto-hide flyout plus the shell flags the rail, mode menu, and agent toggle read.
 * The open flag lives in the panels hook. This frame owns the 500ms hold.
 */
export function ShellChromeProvider({
  autoHide,
  onToggleAutoHide,
  leftDisabledReason,
  agentDisabledReason,
  flyoutOpen,
  onFlyoutOpenChange,
  sidebar,
  children,
}: {
  autoHide: boolean
  onToggleAutoHide: () => void
  leftDisabledReason?: string
  agentDisabledReason?: string
  flyoutOpen: boolean
  onFlyoutOpenChange: (open: boolean) => void
  /** Left sidebar tree. Portaled once so dock and rail share one mount. */
  sidebar?: ReactNode
  children: ReactNode
}) {
  const hold = useShellFlyoutHold(() => onFlyoutOpenChange(false))
  const [flyoutPortal, setFlyoutPortal] = useState<HTMLElement | null>(null)
  const [sidebarSlot, setSidebarSlot] = useState<HTMLElement | null>(null)
  const setPortal = useCallback((node: HTMLElement | null) => {
    setFlyoutPortal(node)
  }, [])
  const setSidebarSlotStable = useCallback((node: HTMLElement | null) => {
    setSidebarSlot(node)
  }, [])

  const onMenuOpenChange = useCallback((open: boolean) => {
    hold.menuOpenChange(open)
  }, [hold])

  const closeFlyoutNow = useCallback(() => {
    hold.closeNow()
  }, [hold])

  /** Open the auto-hide flyout at once. Rail icon clicks use this. Hover waits. */
  const openFlyout = useCallback(() => {
    if (!autoHide) return
    hold.enter()
    onFlyoutOpenChange(true)
  }, [autoHide, hold, onFlyoutOpenChange])

  const onRailEnter = useCallback(() => {
    if (!autoHide) return
    hold.enter()
  }, [autoHide, hold])

  const onRailLeave = useCallback(() => {
    if (!autoHide) return
    hold.leave()
  }, [autoHide, hold])

  const value = useMemo<ShellChromeValue>(() => ({
    autoHide,
    toggleAutoHide: onToggleAutoHide,
    onMenuOpenChange,
    closeFlyoutNow,
    openFlyout,
    onRailEnter,
    onRailLeave,
    leftDisabledReason,
    agentDisabledReason,
    flyoutOpen,
    flyoutPortal,
    setFlyoutPortal: setPortal,
  }), [
    autoHide,
    onToggleAutoHide,
    onMenuOpenChange,
    closeFlyoutNow,
    openFlyout,
    onRailEnter,
    onRailLeave,
    leftDisabledReason,
    agentDisabledReason,
    flyoutOpen,
    flyoutPortal,
    setPortal,
  ])

  return (
    <ShellChromeContext.Provider value={value}>
      <SidebarSlotContext.Provider value={setSidebarSlotStable}>
        {sidebar ? <StablePortal slot={sidebarSlot}>{sidebar}</StablePortal> : null}
        {children}
      </SidebarSlotContext.Provider>
    </ShellChromeContext.Provider>
  )
}

const SidebarSlotContext = createContext<(node: HTMLElement | null) => void>(() => {})

/** Empty box the stable sidebar host attaches to. One slot is mounted at a time. */
export function ShellHostSlot({
  className,
  testId,
}: {
  className?: string
  testId?: string
}) {
  const setSlot = useContext(SidebarSlotContext)
  const ref = useCallback((node: HTMLDivElement | null) => {
    setSlot(node)
  }, [setSlot])
  return <div ref={ref} className={className} data-testid={testId} />
}

/** Drag handle that docks a collapsed column once the pointer crosses its floor. */
function ShellEdgeDrag({
  edge,
  floorPx,
  roomForDock,
  onDock,
  pinOnClick = false,
}: {
  edge: 'left' | 'right'
  floorPx: number
  roomForDock: boolean
  onDock: () => void
  /** Auto-hide: a click pins. The resize cursor is wrong here, and a short drag never docks. */
  pinOnClick?: boolean
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={pinOnClick ? 'Pin sidebar' : edge === 'right' ? 'Resize sidebar' : 'Resize agent'}
      title={pinOnClick ? 'Pin sidebar' : undefined}
      className={cn(
        'group/edge absolute inset-y-0 z-30 w-2',
        pinOnClick ? 'cursor-e-resize' : 'cursor-col-resize',
        edge === 'right' ? '-right-1' : '-left-1',
      )}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        if (pinOnClick) {
          const startX = event.clientX
          const up = (ev: PointerEvent) => {
            window.removeEventListener('pointerup', up)
            if (Math.abs(ev.clientX - startX) < 4) onDock()
          }
          window.addEventListener('pointerup', up)
          return
        }
        event.preventDefault()
        const startX = event.clientX
        let docked = false
        const move = (ev: PointerEvent) => {
          if (docked) return
          const delta = edge === 'right' ? ev.clientX - startX : startX - ev.clientX
          const decision = resolveShellColumnWidth({
            draggedPx: SHELL_ICON_RAIL_PX + delta,
            floorPx,
            docked: false,
            lastExpandedPx: floorPx,
            roomForFloor: roomForDock,
          })
          if (!decision.docked) return
          docked = true
          onDock()
        }
        const up = () => {
          window.removeEventListener('pointermove', move)
          window.removeEventListener('pointerup', up)
        }
        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', up)
      }}
    >
      {pinOnClick ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-2 left-1/2 w-0.5 -translate-x-1/2 rounded-full bg-primary opacity-0 group-hover/edge:opacity-100"
          style={{
            transitionProperty: 'opacity',
            transitionDuration: `${SHELL_PANEL_MOTION_MS}ms`,
            transitionTimingFunction: SHELL_PANEL_EASE,
          }}
        />
      ) : null}
    </div>
  )
}

/** Collapsed sidebar rail. Hover opens the auto-hide flyout. Drag past the floor docks. */
export function ShellCollapsedRail({
  roomForDock,
  onDock,
  children,
}: {
  roomForDock: boolean
  onDock: () => void
  children: ReactNode
}) {
  const chrome = useShellChrome()
  const exitClass = useShellPanelExitClass('left')
  const presence = useShellFlyoutPresence(chrome.autoHide && chrome.flyoutOpen, exitClass)
  const [arming, setArming] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const gestureRef = useRef({
    holdEnter: chrome.onRailEnter,
    leave: chrome.onRailLeave,
    open: chrome.openFlyout,
    flyoutOpen: chrome.flyoutOpen,
  })
  gestureRef.current = {
    holdEnter: chrome.onRailEnter,
    leave: chrome.onRailLeave,
    open: chrome.openFlyout,
    flyoutOpen: chrome.flyoutOpen,
  }
  const enterDelay = useShellFlyoutEnterDelay(() => {
    setArming(false)
    gestureRef.current.open()
  })
  const enterDelayRef = useRef(enterDelay)
  enterDelayRef.current = enterDelay

  useEffect(() => {
    if (!chrome.flyoutOpen) return
    enterDelayRef.current.cancel()
    setArming(false)
  }, [chrome.flyoutOpen])

  // The sidebar is portaled in from outside this React subtree, so React
  // onMouseEnter/Leave would fire on every move into it. Native events follow DOM.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const enter = () => {
      gestureRef.current.holdEnter()
      if (gestureRef.current.flyoutOpen) {
        enterDelayRef.current.cancel()
        setArming(false)
        return
      }
      setArming(true)
      enterDelayRef.current.arm()
    }
    const leave = () => {
      enterDelayRef.current.cancel()
      setArming(false)
      gestureRef.current.leave()
    }
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mouseleave', leave)
    return () => {
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
    }
  }, [])

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative h-full min-h-0 shrink-0',
        SHELL_ICON_RAIL_CLASS,
        presence.mounted && 'z-30',
      )}
      data-testid={testIds.resizableLayout.sidebar}
      data-shell-flyout-root=""
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-2 right-0 z-20 w-0.5 origin-top rounded-full bg-primary"
        style={{
          transform: arming ? 'scaleY(1)' : 'scaleY(0)',
          opacity: arming ? 1 : 0,
          transitionProperty: 'transform, opacity',
          transitionDuration: arming ? `${SHELL_FLYOUT_ENTER_HOLD_MS}ms` : `${SHELL_PANEL_MOTION_MS}ms`,
          transitionTimingFunction: arming ? 'linear' : SHELL_PANEL_EASE,
        }}
      />
      {children}
      <ShellEdgeDrag
        edge="right"
        floorPx={SHELL_LEFT_FLOOR_PX}
        roomForDock={roomForDock}
        onDock={onDock}
        pinOnClick={chrome.autoHide}
      />
      <ShellFlyout
        presence={presence}
        exitClass={exitClass}
        left={SHELL_ICON_RAIL_PX}
        width={FLYOUT_WIDTH_PX}
      >
        <FlyoutPortalSlot />
      </ShellFlyout>
    </div>
  )
}

/** Mount point the collapsed sidebar portals its mode column into. */
function FlyoutPortalSlot() {
  const { setFlyoutPortal } = useShellChrome()
  const ref = useCallback((node: HTMLDivElement | null) => {
    setFlyoutPortal(node)
  }, [setFlyoutPortal])
  return <div ref={ref} className="flex h-full min-h-0 w-full min-w-0 flex-col" />
}

/** Collapsed agent strip. Drag past the floor docks and restores the last width. */
export function ShellCollapsedAgentEdge({
  roomForDock,
  onDock,
  children,
}: {
  roomForDock: boolean
  onDock: () => void
  children: ReactNode
}) {
  return (
    <div
      id="flownotes-chat-panel"
      className={cn(SHELL_ICON_RAIL_CLASS, 'relative h-full min-h-0 shrink-0')}
    >
      <ShellEdgeDrag
        edge="left"
        floorPx={SHELL_AGENT_FLOOR_PX}
        roomForDock={roomForDock}
        onDock={onDock}
      />
      {children}
    </div>
  )
}

/** Clicks on the canvas close the auto-hide flyout without waiting out the hold. */
export function ShellMainDismiss({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  const { closeFlyoutNow } = useShellChrome()
  return (
    <div
      className={className}
      data-testid={testIds.resizableLayout.mainArea}
      onClick={() => closeFlyoutNow()}
    >
      {children}
    </div>
  )
}
